import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { usePageTitle } from "../../lib/usePageTitle";
import {
  getOrCreateSetupState,
  isSetupWizardEnabled,
  markSetupComplete,
  markSetupSkipped,
  saveSetupState,
  type SetupData,
  type SetupStepId,
} from "../../lib/setupState";
import SetupShell from "./SetupShell";
import SetupSummary from "./SetupSummary";
import Step1Business, { validateStep1 } from "./Step1Business";
import Step2TeachAI, { validateStep2 } from "./Step2TeachAI";
import Step3Receptionist, { saveAgentName, validateStep3 } from "./Step3Receptionist";
import Step4Team, { validateStep4 } from "./Step4Team";
import Step5Connect, { validateStep5 } from "./Step5Connect";

function getCustomerId(): string | null {
  return (
    localStorage.getItem("customerId") ||
    sessionStorage.getItem("customerId")
  );
}

const STEP_META: Record<
  SetupStepId,
  { title: string; subtitle: string }
> = {
  1: {
    title: "Tell us about your business",
    subtitle: "Help your AI understand who it's representing.",
  },
  2: {
    title: "Teach your AI about your business",
    subtitle: "Share the knowledge your receptionist needs on day one.",
  },
  3: {
    title: "Meet your receptionist",
    subtitle: "Give your AI a name callers will recognize.",
  },
  4: {
    title: "Who should calls go to?",
    subtitle: "Set up who handles transfers and follow-ups.",
  },
  5: {
    title: "Connect your line.",
    subtitle: "Forward your business number to NeuroSphere.",
  },
};

type View = "wizard" | "summary";

export default function SetupPage() {
  usePageTitle("Setup");
  const navigate = useNavigate();
  const customerId = getCustomerId();

  const [view, setView] = useState<View>("wizard");
  const [step, setStep] = useState<SetupStepId>(1);
  const [setup, setSetup] = useState<SetupData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveAgentError, setSaveAgentError] = useState<string | null>(null);
  const [isSavingAgent, setIsSavingAgent] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!customerId) {
      setHydrated(true);
      return;
    }
    const state = getOrCreateSetupState(customerId);
    setSetup(state);
    setStep(state.currentStep);
    if (state.skipped) setView("summary");
    setHydrated(true);
  }, [customerId]);

  if (!isSetupWizardEnabled()) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!customerId) {
    return <Navigate to="/login" replace />;
  }

  if (!hydrated || !setup) {
    return (
      <div
        className="min-h-screen w-full bg-transparent text-gray-400 flex items-center justify-center px-6"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        Loading setup…
      </div>
    );
  }

  const persist = (next: SetupData) => {
    saveSetupState(customerId, next);
    setSetup(next);
  };

  const validateCurrentStep = (): string | null => {
    switch (step) {
      case 1:
        return validateStep1(setup.steps.business);
      case 2:
        return validateStep2(setup.steps.teach);
      case 3:
        return validateStep3(setup.steps.receptionist);
      case 4:
        return validateStep4(setup.steps.team);
      case 5:
        return validateStep5(setup.steps.connect);
      default:
        return null;
    }
  };

  const completeStep = (nextStep?: SetupStepId) => {
    const completedSteps = setup.completedSteps.includes(step)
      ? setup.completedSteps
      : ([...setup.completedSteps, step] as SetupStepId[]).sort((a, b) => a - b);

    const updated: SetupData = {
      ...setup,
      completedSteps,
      currentStep: nextStep ?? (Math.min(step + 1, 5) as SetupStepId),
    };

    if (step === 5) {
      updated.completedAt = new Date().toISOString();
    }

    persist(updated);
    if (nextStep) setStep(nextStep);
    else if (step < 5) setStep((step + 1) as SetupStepId);
    else setView("summary");
  };

  const handleNext = async () => {
    const validationError = validateCurrentStep();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (step === 3) {
      setIsSavingAgent(true);
      setSaveAgentError(null);
      const ok = await saveAgentName(setup.steps.receptionist?.agentName ?? "");
      setIsSavingAgent(false);
      if (!ok) {
        setSaveAgentError("Failed to save receptionist name. Please try again.");
        return;
      }
    }

    setError(null);
    setSaveAgentError(null);
    completeStep();
  };

  const handleBack = () => {
    setError(null);
    if (step > 1) setStep((step - 1) as SetupStepId);
  };

  const handleSkipLater = () => {
    const skipped = markSetupSkipped(customerId);
    setSetup(skipped);
    setView("summary");
  };

  const handleCompleteSetup = () => {
    const completed = markSetupComplete(customerId);
    setSetup(completed);
    navigate("/dashboard");
  };

  if (view === "summary") {
    return (
      <SetupSummary
        setup={setup}
        onResume={(s) => {
          setView("wizard");
          setStep(s);
          persist({ ...setup, currentStep: s, skipped: false });
        }}
        onGoDashboard={() => navigate("/dashboard")}
      />
    );
  }

  const meta = STEP_META[step];

  return (
    <SetupShell
      step={step}
      title={meta.title}
      subtitle={meta.subtitle}
      onBack={step > 1 ? handleBack : undefined}
      onNext={step < 5 ? handleNext : undefined}
      onSkipLater={handleSkipLater}
      showSkipLater={step <= 4}
      canGoBack={step > 1}
      canGoNext={!isSavingAgent}
      nextLabel="Next"
    >
      {step === 1 && (
        <Step1Business
          data={setup.steps.business}
          onChange={(business) =>
            persist({ ...setup, steps: { ...setup.steps, business } })
          }
          error={error}
        />
      )}
      {step === 2 && (
        <Step2TeachAI
          data={setup.steps.teach}
          onChange={(teach) =>
            persist({ ...setup, steps: { ...setup.steps, teach } })
          }
          onSkip={() => {
            setError(null);
            completeStep();
          }}
          error={error}
        />
      )}
      {step === 3 && (
        <Step3Receptionist
          data={setup.steps.receptionist}
          businessName={setup.steps.business?.businessName}
          onChange={(receptionist) =>
            persist({ ...setup, steps: { ...setup.steps, receptionist } })
          }
          error={error}
          saveError={saveAgentError}
          isSaving={isSavingAgent}
        />
      )}
      {step === 4 && (
        <Step4Team
          data={setup.steps.team}
          onChange={(team) =>
            persist({ ...setup, steps: { ...setup.steps, team } })
          }
          error={error}
        />
      )}
      {step === 5 && (
        <Step5Connect
          data={setup.steps.connect}
          onChange={(connect) =>
            persist({ ...setup, steps: { ...setup.steps, connect } })
          }
          onComplete={handleCompleteSetup}
          error={error}
        />
      )}
    </SetupShell>
  );
}
